import React, { useEffect, useCallback, useContext, useState, useMemo } from 'react';
import appConfig from 'app-config';
import _ from 'lodash';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MLNavigationStepsTrackingService, MLPolicyChangeDataHolderService } from 'ml-services-common-react';
import { MLEndorsementUtil, MLEndorsementFlowUtil, MLProductCodeUtil } from 'ml-portals-util-js';
import { platformMessages } from 'ml-components-platform-react';
import { MLModalService , MLTagManagerService } from 'ml-portals-services-react';

import { MLDriverVehicleUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import {
    MLOutOfSequenceConflictComponent,
    MLPAEditCoveragesComponent,
    MLHOEditCoveragesComponent,
    MLPUPEditCoveragesComponent
} from 'ml-components-common-react';

import metadata from './MLCECoveragesPage.metadata.json5';
import styles from './MLCECoveragesPage.module.scss';
import messages from './MLCECoveragesPage.messages';

import { IntlContext } from '@jutro/legacy/locale';

const emptyString = 'Empty';
const accidentPreventionCodes = ['PADriversRoleModiferPreventionCourse_ML', 'PADriverAccidentPrevention_ML'];
const FUNCTIONAL_REPLACEMENT_COST_TERM_VALUE = 'Functional Replacement Cost';
const VALIDATION_ALERT_NAME = 'validationErrorAlert';

// non-agent ml-coverages.html, MLCoveragesCtrl.js
function MLCECoveragesPage(props) {
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        steps,
        changeNextSteps,
        currentStep
    } = props;

    const isAMP = appConfig.portalCode === 'AMP';
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader } = useAuthentication();
    const intl = useContext(IntlContext);

    const {
        isVehicleEligibleForAccidentPrevention,
        getRatedDriversNonStudent,
        validateVehiclesHaveAssignmentsForEveryDriver,
        validateVehicleAssignmentPercentages
    } = MLDriverVehicleUtil();

    const {
        showProgressDialog
    } = MLModalService();

    const {
        clearBlockAlerts,
        addErrorAlert,
        addWarningAlert,
        clearAlerts
    } = MLNotificationBlockUtil();

    const { submissionVM: endorsementVM } = wizardData;
    const isOOSConflict = _.get(endorsementVM, 'isOutOfSequenceConflict_ML.value');
    const vehicles = _.get(endorsementVM, 'lobData.personalAuto.coverables.vehicles.value', []);
    const vehicleDrivers = _.get(endorsementVM, 'lobData.personalAuto.coverables.vehicleDrivers_Ext', []);
    const jobID = _.get(endorsementVM, 'jobID.value');
    const lobPathString = _.camelCase(_.get(endorsementVM, 'baseData.productName.value', ''));
    const paDrivers = _.get(endorsementVM, 'lobData.personalAuto.coverables.drivers.value')
    const ratedDrivers = paDrivers ? paDrivers.filter((driver) => driver.type_ML === 'rated') : undefined;
    const hoCoveragePartType = _.get(endorsementVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code', '');
    const productCode = _.get(endorsementVM, 'baseData.productCode.value', '');

    const isAgent = appConfig.agentendorsement === 'true';

    const paModel = _.get(endorsementVM, 'lobData.personalAuto.value');
    const isPersonalAuto = endorsementVM.value.isPersonalAuto();
    const isHomeOwners = endorsementVM.value.isHomeOwners()

    const [primaryVehiclesDefaulted, setPrimaryVehiclesDefaulted] = useState(false);
    const [defaultedDriverPrimaryVehicles] = useState([]);
    const [primaryDriversDefaulted, setPrimaryDriversDefaulted] = useState(false);
    const [disableContinueForValidationErrors, setDisableContinueForValidationErrors] = useState(false);
    const [vehicleDriversRestored, setVehicleDriversRestored] = useState(false);
    const [primaryVehiclesRestored, setPrimaryVehiclesRestored] = useState(false);

    const formatAmountString = useCallback((amount) => {
        const premium = _.get(amount, 'amount', 0);
        const currency = _.get(amount, 'currency', 'usd');

        try {
            return intl.formatNumber(premium, { style: 'currency', currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const getEstimatedReplacementCostMessage = useCallback(() => {
        const messageHelpObj = {};
        if (endorsementVM.value.isHomeOwners()) {
            const replacementCost = _.get(endorsementVM, 'lobData.homeowners.coverables.constructionDetails_ML.replacementCost.value');
            const cost = intl.formatNumber(replacementCost, { style: 'currency', currency: 'USD' });
            if (MLProductCodeUtil.isHomeOwnersPartialTypeRental(hoCoveragePartType)) {
                messageHelpObj.message = translator(messages.estimatedReplacementCostMsg, { replacementCost: cost });
                messageHelpObj.help = translator(messages.rentersEstimatedReplacementCostHelpText);
            } else if (MLProductCodeUtil.isHomeOwnersPartialTypeHome(hoCoveragePartType)) {
                messageHelpObj.message = translator(messages.dwellingReplacementCostMsg, {replacementCost: cost});
                messageHelpObj.help = translator(messages.homeEstimatedReplacementCostHelpText);
            } else if (MLProductCodeUtil.isHomeOwnersPartialTypeCondo(hoCoveragePartType)) {
                messageHelpObj.message = translator(messages.dwellingReplacementCostMsg, {replacementCost: cost});
                messageHelpObj.help = translator(messages.condoEstimatedReplacementCostHelpText);
            }
        }
        return messageHelpObj;
    }, [endorsementVM, hoCoveragePartType, intl, translator]);

    const [replacementCostLabel] = useState(getEstimatedReplacementCostMessage());

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const getQuoteCoverages = useCallback(() => {
        const offerings = _.get(endorsementVM, `lobData.${lobPathString}.offerings.value`, []);
        const selectedOffering = offerings[0];
        return _.get(selectedOffering, 'coverages');
    }, [endorsementVM, lobPathString]);

    // TODO : TA105643 : remove when PC rules are implemented
    const validatePANamedExclude = useCallback((callback) => {
        if (!isPersonalAuto) {
            return callback(true);
        }
        const currentCoverages = getQuoteCoverages();
        const vehicleCoverages = _.get(currentCoverages, 'vehicleCoverages');
        // named non owner / no vehicle
        if (!vehicleCoverages) {
            return callback(true);
        }

        let isValid = true;
        vehicleCoverages.forEach((vehCov) => {
            const alertName = `paNamedExclude${vehCov.fixedId}`;
            clearBlockAlerts(alertName);
            const paNamedExcludeAllSelectedMessage = {...messages.paNamedExcludeAllSelectedMessage, args: { vehicleName: vehCov.vehicleName }};
            const paNamedExcludeNoneSelectedMessage = {...messages.paNamedExcludeNoneSelectedMessage, args: { vehicleName: vehCov.vehicleName }};
            const selectedNamedExcludeCov = vehCov.coverages.find((cov) => cov.code_ML === 'PAExcludeNamedDriverVehicleLevel_ML' && cov.selected);
            if (selectedNamedExcludeCov) {
                const drivers = selectedNamedExcludeCov.scheduledItems_ML.excludeNamedDrivers;
                const driversSelected = drivers.filter((d) => d.driverExcluded);
                if (driversSelected.length === 0) {
                    addErrorAlert(paNamedExcludeNoneSelectedMessage, alertName);
                    isValid = false;
                }
                if (driversSelected.length === drivers.length) {
                    addErrorAlert(paNamedExcludeAllSelectedMessage, alertName);
                    isValid = false;
                }
            }
        });
        return callback(isValid);

    }, [addErrorAlert, clearBlockAlerts, getQuoteCoverages, isPersonalAuto]);

    const isSuspendLiabilitySelected = useCallback(() => {
        const alertName = 'suspendLiabilitySelected';
        clearBlockAlerts(alertName);
        if (isPersonalAuto) {
            const currentCoverages = getQuoteCoverages();
            currentCoverages.vehicleCoverages.forEach((vehicleCoverage) => {
                vehicleCoverage.coverages.forEach((coverage) => {
                    if (coverage.name === 'Suspend Liability' && coverage.selected === true) {
                        addWarningAlert(messages.suspendedLiability, alertName);
                        window.scrollTo(0, 0);
                    }
                });
            });
        }
    }, [addWarningAlert, clearBlockAlerts, getQuoteCoverages, isPersonalAuto]);

    const isDwelling = useCallback(() => {
        return endorsementVM.value.isDwelling();
    }, [endorsementVM]);

    const isLandlordHome = useCallback(() => {
        return endorsementVM.value.isLandlordHome();
    }, [endorsementVM]);

    const shouldTestFunctionalReplacementCost = (details) => {
        return details && details.yearBuilt < 1986 && details.isReplacementCostSelected_ML !== undefined;
    };

    const updateFunctionalReplacementCostWarning = useCallback(() => {
        let showWarning = false;

        const constructionDetails = _.get(endorsementVM, 'lobData.homeowners.coverables.constructionDetails_ML.value');
        if ((isDwelling() || isLandlordHome()) && shouldTestFunctionalReplacementCost(constructionDetails)) {
            clearBlockAlerts(FUNCTIONAL_REPLACEMENT_COST_TERM_VALUE);
            const isDwellingReplacementCostSelected = constructionDetails.isReplacementCostSelected_ML;
            let isCoverageFunctionalReplacementCostSelected = false;

            const currentCoverages = getQuoteCoverages();
            if (currentCoverages) {
                currentCoverages.baseCoverages.forEach((basecoverage) => {
                    if (basecoverage.code_ML === 'HOPCovA') {
                        basecoverage.terms.forEach((hopcovaterm) => {
                            if ((hopcovaterm.codeIdentifier_ML === 'HOPCovAValuation') && (hopcovaterm.chosenTermValue === FUNCTIONAL_REPLACEMENT_COST_TERM_VALUE)) {
                                isCoverageFunctionalReplacementCostSelected = true;
                            }
                        });
                    }
                });
                showWarning = isDwellingReplacementCostSelected !== isCoverageFunctionalReplacementCostSelected;
            }
        }
        if (showWarning) {
            addWarningAlert(messages.functionalReplacementCostWarning, 'functionalReplacementCostWarning');
            window.scrollTo(0, 0);
        }
    }, [addWarningAlert, clearBlockAlerts, endorsementVM, getQuoteCoverages, isDwelling, isLandlordHome]);

    const isQuoteStale = () => {
        return _.get(endorsementVM, 'value.quoteStatus.isStale', false);
    };

    const markStale = useCallback(() => {
        _.set(endorsementVM, 'value.quoteStatus.isStale', true);
    }, [endorsementVM]);

    const formatValidationErrors = useCallback((errors) => {
        let msg = '';
        if (errors.length > 1) {
            msg = `${translator(messages.addressIssues)}<br></br><br></br>`;
        }
        errors.forEach((error) => {
            msg += `${error}<br></br>`;
        });
        if (isAgent) {
            setDisableContinueForValidationErrors(true);
        }
        const fixMsg = translator(messages.navigateToDiffScreen);
        msg = `${msg}<br></br>${fixMsg}`;

        return {
            id: 'ml.endorsement.common.MLCoveragePage.validationErrors',
            defaultMessage: msg
        };
    }, [isAgent, translator]);

    const onClickHere = useCallback(() => {
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep('endorsementAdditionalChanges', steps, currentStepIndex, changeNextSteps, goNext, productCode)
    }, [changeNextSteps, currentStepIndex, goNext, productCode, steps]);

    const setValidationError = useCallback((errors) => {
        if (!_.isEmpty(errors)) {
            _.uniq(errors).forEach((error, index) => {
                const errormessage = error.split(': ')[1];
                const alertName = `validation-${index}`;
                if(errormessage && !_.isEmpty(errormessage)) {
                    addErrorAlert(errormessage, alertName);
                } else {
                    addErrorAlert(error, alertName);
                }
            });
        }
    }, [addErrorAlert]);

    const setValidationErrorAlerts = useCallback((endorsement) => {
        clearBlockAlerts(VALIDATION_ALERT_NAME);
        if (endorsement.validationErrors_ML) {
            setValidationError(endorsement.validationErrors_ML);
            endorsement.validationErrors_ML = null;
        } else {
            setDisableContinueForValidationErrors(false);
        }
    }, [addErrorAlert, clearBlockAlerts, setValidationError, onClickHere]);

    const getPolicyChangeCoverages = useCallback((policyChange) => {
        const offerings = _.get(policyChange, `lobData.${lobPathString}.offerings`, []);
        const selectedOffering = offerings[0];
        return _.get(selectedOffering, 'coverages');
    }, [lobPathString]);

    const getQuoteCoveragesVM = useCallback((quote) => {
        const offeringsVM = _.get(endorsementVM, `lobData.${lobPathString}.offerings.children`, []);
        const selectedOffering = quote ? offeringsVM.find((o) => o.branchCode.value === quote.branchCode && o.branchName.value === quote.branchName) : offeringsVM[0];
        return _.get(selectedOffering, 'coverages');
    }, [endorsementVM, lobPathString]);

    const updateCoverages = useCallback((callback, showValidationError) => {
        isSuspendLiabilitySelected();
        clearAlerts();
        _.set(endorsementVM, 'value.quoteStatus.showLoading', true);

        const coverages = getQuoteCoverages();
        let data;
        if (isHomeOwners) {
            data = { homeowners : coverages };
        } else if (isPersonalAuto) {
            data = { personalAuto : coverages };
        } else {
            data = { personalUmbrella : coverages }
        }

        const operation = EndorsementService.updateCoverages(jobID, data, authHeader);
        showProgressDialog(platformMessages.loading, operation, true);
        operation.then((policyChange) => {
            endorsementVM.isCoverageChanged_ML.value = policyChange.isCoverageChanged_ML;
            endorsementVM.validationErrors_ML.value = policyChange.validationErrors_ML;
            const coveragesVM = getQuoteCoveragesVM();
            coveragesVM.value = getPolicyChangeCoverages(policyChange);

            if (!!showValidationError && endorsementVM.validationErrors_ML.value && endorsementVM.value.validationErrors_ML.length > 0) {
                setValidationErrorAlerts(endorsementVM.value);
            }
            updateWizardSubmissionData(endorsementVM);
        });
        operation.finally(()=> {
            _.set(endorsementVM, 'value.quoteStatus.showLoading', false);
            // $scope.form.submitted = false;

            if ((!endorsementVM.validationErrors_ML.value || endorsementVM.validationErrors_ML.value.length < 1) && !!callback) {
                callback();
            }
            updateWizardSubmissionData(endorsementVM);
        });
    }, [EndorsementService, authHeader, clearAlerts, endorsementVM, getPolicyChangeCoverages, getQuoteCoverages, getQuoteCoveragesVM, isHomeOwners, isPersonalAuto, isSuspendLiabilitySelected, jobID, setValidationErrorAlerts, showProgressDialog, updateWizardSubmissionData]);

    const validateInputFields = useCallback(()=> {
        if (!endorsementVM.aspects.valid || !endorsementVM.aspects.subtreeValid) {
            return false;
        }

        return true;
    }, [endorsementVM.aspects.subtreeValid, endorsementVM.aspects.valid]);

    const syncCoverage = useCallback((callback, showValidationError)=> {
        markStale();
        if (!validateInputFields()) {
            return;
        }
        updateCoverages(callback, showValidationError === undefined ? false : showValidationError);
    }, [markStale, updateCoverages, validateInputFields]);

    const updateQuote = useCallback((callback) => {
        markStale();

        if (!validateInputFields()) {
            return;
        }
        _.set(endorsementVM, 'value.quoteStatus.showLoading', true);
        isSuspendLiabilitySelected();

        const quotePolicyChangePromise = EndorsementService.quoteEndorsement([endorsementVM.jobID.value], authHeader);
        showProgressDialog(messages.quotingPolicyChange, quotePolicyChangePromise, true);
        quotePolicyChangePromise.then((policyChange) => {
            const newPolicyChange = new PolicyChange(policyChange);
            Object.assign(endorsementVM.value, newPolicyChange);
            updateFunctionalReplacementCostWarning();
            _.set(endorsementVM, 'value.quoteStatus.isStale', false);
            updateWizardSubmissionData(endorsementVM);
        }).catch((error) => {
            if (isAMP && !endorsementVM.validationErrors_ML.value) {
                // When encounter UW issue in requote, redirect to Contact Us page
                Promise.reject(error);
            }
        }).finally(() => {
            _.set(endorsementVM, 'value.quoteStatus.showLoading', false);
            if (callback) {
                callback();
            }
        });
    }, [EndorsementService, authHeader, endorsementVM, isAMP, isSuspendLiabilitySelected, markStale, showProgressDialog, updateFunctionalReplacementCostWarning, updateWizardSubmissionData, validateInputFields]);

    const reQuote = useCallback(()=> {
        validatePANamedExclude((isValid) => {
            if (isValid) {
                syncCoverage(() => {
                    updateQuote();
                }, true);
            }
        });
    }, [syncCoverage, updateQuote, validatePANamedExclude]);

    const stickyBarInfo = () => {
        const initQuoteStatus = {
            isStale: false,
            showLoading: false
        };
        _.set(endorsementVM, 'value.quoteStatus', initQuoteStatus);
        const paymentDetails = _.get(endorsementVM, 'value.paymentDetails_ML');
        endorsementVM.value.selectedPaymentPlan = paymentDetails.paymentPlans_ML.find((plan) => {
            return plan.billingId === paymentDetails.selectedPaymentPlan;
        });
    };

    const isAccidentPreventionSelected = useCallback(() => {
        let accidentPreventionSelected = false;
        if (ratedDrivers) {
            ratedDrivers.forEach((driver) => {
                if (driver.driverModifiers_ML.modifiers) {
                    driver.driverModifiers_ML.modifiers.forEach((modifier) => {
                        if (accidentPreventionCodes.indexOf(modifier.codeIdentifier_ML) > -1) {
                            if (modifier.chosenTermValue) {
                                accidentPreventionSelected = true;
                            }
                        }
                    });
                }
            });
        }
        return accidentPreventionSelected;
    }, [ratedDrivers]);

    const hasAccidentPreventionWithoutPrimaryVehicle = useCallback((driver) => {
        let needsAssignment = false;
        if (driver.driverModifiers_ML.modifiers) {
            driver.driverModifiers_ML.modifiers.forEach((modifier) => {
                if (accidentPreventionCodes.indexOf(modifier.codeIdentifier_ML) > -1) {
                    if (modifier.chosenTermValue) {
                        needsAssignment = !driver.driverModifiers_ML.primaryVehicleFixedId;
                    }
                }
            });
        }
        return needsAssignment;
    }, []);

    const isDriverPrimaryVehicleNeeded = useCallback(() => {
        // check to see if there are any drivers with accident prevention selected but no primary vehicle selected
        let primaryVehicleDefaultingRequired = false;
        if (endorsementVM.baseData.isDriverPrimaryVehicleRequiredState_Ext.value) {
            if (isAccidentPreventionSelected()) {
                ratedDrivers.forEach((driver) => {
                    if (hasAccidentPreventionWithoutPrimaryVehicle(driver)) {
                        primaryVehicleDefaultingRequired = true;
                    }
                });
            }
        }
        return primaryVehicleDefaultingRequired;
    }, [endorsementVM.baseData.isDriverPrimaryVehicleRequiredState_Ext.value, hasAccidentPreventionWithoutPrimaryVehicle, isAccidentPreventionSelected, ratedDrivers]);

    const defaultDriverPrimaryVehicles = useCallback(() => {
        setPrimaryVehiclesDefaulted(true);
        let vehicleIndex = 0;
        const eligibleVehicles = vehicles.filter((veh) => isVehicleEligibleForAccidentPrevention(veh));
        ratedDrivers.forEach((driver) => {
            if (hasAccidentPreventionWithoutPrimaryVehicle(driver)) {
                driver.driverModifiers_ML.modifiers.forEach((modifier) => {
                    if (accidentPreventionCodes.indexOf(modifier.codeIdentifier_ML) > -1 && !driver.driverModifiers_ML.primaryVehicleFixedId) {
                        driver.driverModifiers_ML.primaryVehicleFixedId = eligibleVehicles[vehicleIndex].fixedId;
                        defaultedDriverPrimaryVehicles.push(driver.fixedId);
                    }
                });
                if (vehicleIndex < eligibleVehicles.length - 1) {
                    vehicleIndex++;
                } else {
                    vehicleIndex = 0;
                }
            }
        });
    }, [defaultedDriverPrimaryVehicles, hasAccidentPreventionWithoutPrimaryVehicle, isVehicleEligibleForAccidentPrevention, ratedDrivers, vehicles]);

    const areVehiclePrimaryDriverAssignmentsInvalid = useCallback(() => {
        let areAssignmentsInvalid = false;
        const eligibleVehicles = vehicles.filter((veh) => isVehicleEligibleForAccidentPrevention(veh));
        if (eligibleVehicles.length > 0) {
            eligibleVehicles.forEach((vehicle) => {
                const vehDrivers = vehicleDrivers.filter((vd) => vehicle.fixedId === vd.vehicleID.value);
                if (vehDrivers.length !== 1) {
                    areAssignmentsInvalid = true;
                } else if (vehDrivers[0].percentageDriven_Ext.value !== 100) {
                    areAssignmentsInvalid = true;
                }
            });
        }
        return areAssignmentsInvalid;
    }, [isVehicleEligibleForAccidentPrevention, vehicleDrivers, vehicles]);

    const areVehiclePrimaryDriverAssignmentPercentagesInvalid = useCallback(() => {
        let areAssignmentsInvalid = false;
        const primaryDriverList = [];
        const eligibleVehicles = vehicles.filter((veh) => isVehicleEligibleForAccidentPrevention(veh));
        const ratedDriversNonStudent = getRatedDriversNonStudent(ratedDrivers);
        if (eligibleVehicles.length > 0) {
            if (!validateVehiclesHaveAssignmentsForEveryDriver(eligibleVehicles, ratedDrivers, vehicleDrivers)) {
                areAssignmentsInvalid = true;
            }

            if (!validateVehicleAssignmentPercentages(eligibleVehicles, vehicleDrivers)) {
                areAssignmentsInvalid = true;
            }

            // capture primary drivers for all eligible vehicles
            eligibleVehicles.forEach((vehicle) => {
                const vehicleAssignments = vehicleDrivers.filter((a) => a.vehicleID.value === vehicle.fixedId);
                if (vehicleAssignments.length > 0) {
                    const max = Number(Math.max(...vehicleAssignments.map((ar) => ar.percentageDriven_Ext.value)));
                    primaryDriverList.push(vehicleAssignments.find((t) => parseInt(t.percentageDriven_Ext.value) === max).driverID.value);
                }
            });

            if (ratedDriversNonStudent.length >= eligibleVehicles.length) {
                // check that driver isn't primary on more than one vehicle
                if (primaryDriverList.length !== new Set(primaryDriverList).size) {
                    areAssignmentsInvalid = true;
                }
            } else {
                // check that each rated driver is primary on at least one vehicle
                ratedDriversNonStudent.forEach((driver) => {
                    if (!primaryDriverList.includes(driver.fixedId)) {
                        areAssignmentsInvalid = true;
                    }
                });
            }
        } else if (vehicleDrivers.length > 0) {
            areAssignmentsInvalid = true;
        }
        return areAssignmentsInvalid;
    }, [getRatedDriversNonStudent, isVehicleEligibleForAccidentPrevention, ratedDrivers, validateVehicleAssignmentPercentages, validateVehiclesHaveAssignmentsForEveryDriver, vehicleDrivers, vehicles]);

    const isVehiclePrimaryDriverDefaultingNeeded = useCallback(() => {
        let primaryDriverDefaultingRequired = false;
        if (_.get(endorsementVM, 'baseData.isVehiclePrimaryDriverState_Ext.value')) {
            if (isAccidentPreventionSelected(ratedDrivers)) {
                if (areVehiclePrimaryDriverAssignmentsInvalid()) {
                    primaryDriverDefaultingRequired = true;
                }
            }
        } else if (_.get(endorsementVM, 'baseData.isVehiclePrimaryDriverPercentageState_Ext.value')) {
            if (isAccidentPreventionSelected(ratedDrivers)) {
                if (areVehiclePrimaryDriverAssignmentPercentagesInvalid()) {
                    primaryDriverDefaultingRequired = true;
                }
            }
        }
        return primaryDriverDefaultingRequired;
    }, [areVehiclePrimaryDriverAssignmentPercentagesInvalid, areVehiclePrimaryDriverAssignmentsInvalid, endorsementVM, isAccidentPreventionSelected, ratedDrivers]);

    const defaultVehiclePrimaryDrivers = useCallback(() => {
        if (_.get(endorsementVM, 'baseData.isVehiclePrimaryDriverState_Ext.value')) {
            setPrimaryDriversDefaulted(true);
            let driverIndex = 0;
            const eligibleVehicles = vehicles.filter((veh) => isVehicleEligibleForAccidentPrevention(veh));
            vehicleDrivers.value = [];
            eligibleVehicles.forEach((veh) => {
                const vehicleDriver = viewModelService.create({}, 'pc', 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.VehicleDriverDTO');
                vehicleDriver.vehicleID.value = veh.fixedId;
                if (driverIndex >= ratedDrivers.length) {
                    driverIndex = 0;
                }
                vehicleDriver.driverID.value = ratedDrivers[driverIndex].fixedId;
                vehicleDriver.percentageDriven_Ext.value = 100;
                vehicleDrivers.pushElement(vehicleDriver.value);
                driverIndex++;
            });
        } else if (_.get(endorsementVM, 'baseData.isVehiclePrimaryDriverPercentageState_Ext.value')) {
            setPrimaryDriversDefaulted(true);
            let driverIndex = 0;
            const eligibleVehicles = vehicles.filter((veh) => isVehicleEligibleForAccidentPrevention(veh));
            vehicleDrivers.value = [];
            eligibleVehicles.forEach((veh) => {
                const vehicleDriver = viewModelService.create({}, 'pc', 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.VehicleDriverDTO');
                vehicleDriver.vehicleID.value = veh.fixedId;
                if (driverIndex >= ratedDrivers.length) {
                    driverIndex = 0;
                }
                vehicleDriver.driverID.value = ratedDrivers[driverIndex].fixedId;
                vehicleDriver.percentageDriven_Ext.value = 100;
                vehicleDrivers.pushElement(vehicleDriver.value);

                ratedDrivers.forEach((drv) => {
                    if (drv.fixedId !== vehicleDriver.driverID.value) {
                        const zeroVehicleDriver = viewModelService.create({}, 'pc', 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.VehicleDriverDTO');
                        zeroVehicleDriver.vehicleID.value = veh.fixedId;
                        zeroVehicleDriver.driverID.value = drv.fixedId;
                        zeroVehicleDriver.percentageDriven_Ext.value = 0;
                        vehicleDrivers.pushElement(zeroVehicleDriver.value);
                    }
                });
                driverIndex++;
            });
        }
    }, [endorsementVM, isVehicleEligibleForAccidentPrevention, ratedDrivers, vehicleDrivers, vehicles, viewModelService]);

    const updatePrimaryVehicleDrivenIfNeeded = useCallback(() => {
        let updateNeeded = false;
        if (isDriverPrimaryVehicleNeeded()) {
            defaultDriverPrimaryVehicles();
            updateNeeded = true;
        }
        if (isVehiclePrimaryDriverDefaultingNeeded()) {
            defaultVehiclePrimaryDrivers();
            updateNeeded = true;
        }
        if (updateNeeded) {
            const updateModifier = EndorsementService.updateModifiers(_.cloneDeep(endorsementVM.value, authHeader));
            showProgressDialog(messages.savingPolicyChange, updateModifier, true);
        }
    }, [EndorsementService, authHeader, defaultDriverPrimaryVehicles, defaultVehiclePrimaryDrivers, endorsementVM.value, isDriverPrimaryVehicleNeeded, isVehiclePrimaryDriverDefaultingNeeded, showProgressDialog]);

    useEffect(() => {
        MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.updatedCoverages), false, currentStep.mlStepName, authHeader);

        stickyBarInfo();
        isSuspendLiabilitySelected();
        updateFunctionalReplacementCostWarning();
        if (isPersonalAuto) {
            updatePrimaryVehicleDrivenIfNeeded();
        }
        if (MLPolicyChangeDataHolderService.getPageAction() && MLPolicyChangeDataHolderService.getPageAction() === 'requote') {
            reQuote();
            MLPolicyChangeDataHolderService.setPageAction('');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const restoreVehicleDriverValues = useCallback(() => {
        if (primaryDriversDefaulted) {
            const vehicleDriversValueOnLoad = [...vehicleDrivers.value];
            vehicleDrivers.value = vehicleDriversValueOnLoad;
            setVehicleDriversRestored(true);
        }
    }, [primaryDriversDefaulted, vehicleDrivers]);

    const restoreDriverPrimaryVehicles = useCallback(() => {
        if (primaryVehiclesDefaulted) {
            defaultedDriverPrimaryVehicles.forEach((driverId) => {
                const driver = paDrivers.find((drv) => drv.fixedId === driverId);
                driver.driverModifiers_ML.primaryVehicleFixedId = 0;
            });
            setPrimaryVehiclesRestored(true);
        }
    }, [defaultedDriverPrimaryVehicles, paDrivers, primaryVehiclesDefaulted]);

    const completeCurrentStepAndGoNext = useCallback(() => {
        const nextStepID = 'endorsementAdditionalChanges';
        const lbl = MLEndorsementUtil.stepLabel(currentStep);
        MLEndorsementUtil.setEndorsementDescription(lbl, endorsementVM, translator);
        MLNavigationStepsTrackingService.completeCurrentStep(null, authHeader);
        MLTagManagerService.sendFormStepPolicyChangeStep();
        restoreVehicleDriverValues();
        restoreDriverPrimaryVehicles();

        MLEndorsementFlowUtil.addAndNavigateToSelectedStep(nextStepID, steps, currentStepIndex, changeNextSteps, goNext, productCode) 
    }, [authHeader, changeNextSteps, currentStep, currentStepIndex, endorsementVM, goNext, productCode, restoreDriverPrimaryVehicles, restoreVehicleDriverValues, steps, translator]);

    const onNext = useCallback(() => {
        validatePANamedExclude((isValid) => {
            if (isValid) {
                if ( _.get(endorsementVM, 'value.quoteStatus.isStale', false)) {
                    syncCoverage(() => {
                        updateQuote(completeCurrentStepAndGoNext);
                    }, true);
                } else {
                    completeCurrentStepAndGoNext();
                }
            } else {
                window.scrollTo(0, 0);
            }
        });
    }, [completeCurrentStepAndGoNext, endorsementVM, syncCoverage, updateQuote, validatePANamedExclude]);

    const isAbleToQuote = useMemo(() => {
        return !(endorsementVM.isOutOfSequenceConflict_ML.value);
    }, [endorsementVM.isOutOfSequenceConflict_ML.value]);

    const isQuotingRequired = useMemo(() => {
        return  _.get(endorsementVM, 'status.value.code') === 'Draft';
    }, [endorsementVM]);

    const getMonthlyPremium = useCallback(() => {
        const paymentPlan = _.get(endorsementVM, 'paymentDetails_ML.paymentPlans_ML.value', []).filter((plan) => {
            return plan.billingId === 'pp:3';
        });

        // if there's no monthly payment plan, display totalMonthlyPremium_ML
        return paymentPlan[0] && paymentPlan[0].installment ? formatAmountString(paymentPlan[0].installment) : formatAmountString(endorsementVM.totalMonthlyPremium_ML.value);
    }, [endorsementVM, formatAmountString]);

    const overrideProps = {
        mlOosConflict: {
            isOOSConflict,
            isAgent
        },
        paEditCoverages: {
            visible: !_.isUndefined(paModel),
            model: getQuoteCoverages(),
            loading: _.get(endorsementVM, 'value.quoteStatus.showLoading', false),
            onCovChange: syncCoverage,
            onGeneralChange: markStale,
            isDisplayOnly: false,
            // addVehicle: addVehicle,
            // editVehicle: editVehicle,
            // removeVehicle: removeVehicle,
            // editCoverage: editCoverage,
            // hideEditLinks: hideEditLinks,
            // hideCoverageButton: hideEditCoverage,
            // selectedTermType: selectedTermType
        },
        homeownersCoverages: {
            visible:  !_.isUndefined(_.get(endorsementVM, 'lobData.homeowners.value'))
        },
        hoEditCoveragesComponent: {
            visible: !_.isUndefined(_.get(endorsementVM, 'lobData.homeowners.value')),
            loading: _.get(endorsementVM, 'value.quoteStatus.showLoading', false),
            onCovChange: syncCoverage,
            replacementCostMsg: replacementCostLabel,
            onGeneralChange: markStale,
            isQuoteStale,
            isEndorsement: true,
            model: getQuoteCoverages(),
            isDisplayOnly: false
        },
        pupCoverages: {
            visible: !_.isUndefined(_.get(endorsementVM, 'lobData.personalUmbrella.value'))
        },
        pupEditCoveragesComponent: {
            model: getQuoteCoverages(),
            loading: _.get(endorsementVM, 'value.quoteStatus.showLoading', false),
            onCovChange: syncCoverage,
            onGeneralChange: markStale,
            isDisplayOnly: false
        },
        monthlyPremium: {
            content: translator(messages.premiumPerMonth, { premium: getMonthlyPremium() }),
            className: isQuotingRequired ? "premium-strike subsection-header" : "subsection-header"
        },
        stateFeesComppmemt: {
            visible: _.get(endorsementVM, 'value.totalChanges_Ext.amount', 0) !== 0,
            totalFees: _.get(endorsementVM, 'totalChanges_Ext.value', {}),
            itemizedFees: _.get(endorsementVM, 'feesCosts_Ext.value', []),
            displayAlternativeTemplate: true
        },
        requoteButton: {
            visible: isQuoteStale(),
            disabled: !isAbleToQuote,
            onClick: reQuote,
            mlTagManagerLinkClick: "Updated Coverages_Recalculate Your Premium"
        },
        nextButton: {
            visible: !isQuoteStale(),
            onClick: onNext
        },
        nextDescription: {
            visible: !isQuoteStale(),
            content: (
                <FormattedMessage
                    {...messages.nextDesc}
                    values={{
                        additionalChangesNeeded: (
                            <strong>{translator(messages.additionalChangesNeeded)}</strong>
                        )
                    }}
                />)
        },
        nextDescriptionChangesMade: {
            visible: isQuoteStale()
        },
        stickyFooterContainer: {
            visible: !isAgent
        }
    };

    const resolvers = {
        resolveComponentMap: {
            mlOutOfSequenceConflictComponent: MLOutOfSequenceConflictComponent,
            mlpupeditcoveragescomponent: MLPUPEditCoveragesComponent,
            mlpaeditcoveragescomponent: MLPAEditCoveragesComponent,
            mlhoeditcoveragescomponent: MLHOEditCoveragesComponent
        }
    };

    return (
        <WizardPage
            policyChangePageHeaderML={messages.editCoverages}
            showCancel={false}
            showNext={false}
            showPrevious={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={endorsementVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

MLCECoveragesPage.propTypes = wizardProps;
export default MLCECoveragesPage;